import { infiniteQueryOptions } from '@tanstack/react-query';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';

import {
  MarketplaceBrowseDocument,
  type MarketplaceBrowseListingCardFragment,
  type MarketplaceBrowseListingLabelsFragment,
  MarketplaceBrowseMapDocument,
  type MarketplaceBrowseMapQuery,
  type MarketplaceBrowseMapQueryVariables,
  type MarketplaceBrowseQuery,
  type MarketplaceBrowseQueryVariables,
  MarketplaceBrowseRefreshDocument,
  type MarketplaceGeoPointInput,
  type MarketplaceListingFiltersInput,
} from './__generated__/marketplace-browse.zeus';

type MarketplaceMapMarkersResult = Extract<
  MarketplaceBrowseMapQuery['marketplace']['mapSearch'],
  { __typename: 'MarketplaceMapSearchMarkersResult' }
>;

type MarketplaceMapClustersResult = Extract<
  MarketplaceBrowseMapQuery['marketplace']['mapSearch'],
  { __typename: 'MarketplaceMapSearchClustersResult' }
>;

export type MarketplaceBrowseMapMarkers = MarketplaceMapMarkersResult['markers'];

/** Server clusters that hold more than one listing. Singletons become markers. */
type MarketplaceBrowseMapClusters = MarketplaceMapClustersResult['clusters'];

/**
 * `markers` asks Zeus for every pin in the area. `adaptive` lets Zeus return
 * geohash clusters for dense viewports instead, the mode the iOS map uses.
 */
export type MarketplaceMapMode = 'markers' | 'adaptive' | 'screen' | 'unclustered';

export type MarketplaceBrowseListings = Omit<
  MarketplaceBrowseQuery['marketplace']['listings'],
  'listings'
> & {
  listings: Array<
    MarketplaceBrowseListingCardFragment & Partial<MarketplaceBrowseListingLabelsFragment>
  >;
};

type MarketplaceInitialBrowsePage = {
  kind: 'initial';
  listings: MarketplaceBrowseListings;
  mapMarkers: MarketplaceBrowseMapMarkers | null;
  mapClusters: MarketplaceBrowseMapClusters | null;
};

export type MarketplaceBrowsePage =
  | MarketplaceInitialBrowsePage
  | { kind: 'more'; listings: MarketplaceBrowseListings };

type MarketplaceBrowseRequest = typeof requestZeusFromBrowser;

type MarketplaceBrowseRequestOptions = {
  cachedListings?: MarketplaceBrowseListings;
  mapQueryClient?: QueryClient;
  onMapResult?: (result: MarketplaceMapResult) => void;
  /** Abort signal owned by the active TanStack query. */
  signal: AbortSignal;
  /** Search inputs shared by the list and map projections. */
  variables: MarketplaceBrowseVariables;
  /** Typed graph request implementation, replaceable by tests and SSR. */
  request: MarketplaceBrowseRequest;
  /** How the map projection is requested. */
  mapMode: MarketplaceMapMode;
  /** Visible polygon for adaptive mode when the search area itself is not one. */
  mapViewport?: MarketplaceGeoPointInput[];
};

type MarketplaceBrowseQueryOptions = {
  mapQueryClient?: QueryClient;
  onMapResult?: (result: MarketplaceMapResult) => void;
  /** Search inputs included in the TanStack query key. */
  variables: MarketplaceBrowseVariables;
  /** Optional request implementation for SSR and tests. */
  request?: MarketplaceBrowseRequest;
  /** Defaults to individual markers, the legacy map's mode. */
  mapMode?: MarketplaceMapMode;
  /**
   * Adaptive mode needs a polygon. Market and zone searches have none, so the
   * caller passes the area the map shows on load, usually the market's box.
   */
  mapViewport?: MarketplaceGeoPointInput[];
};

export type MarketplaceBrowseVariables = {
  listInput: MarketplaceBrowseQueryVariables['input'];
  mapInput?: MarketplaceBrowseMapQueryVariables['input'];
};

export const marketplaceBrowseQueryKey = ['marketplace', 'browse'] as const;
export const marketplaceMapQueryKey = ['marketplace', 'map'] as const;

export type MarketplaceMapResult = {
  markers: MarketplaceBrowseMapMarkers;
  clusters: MarketplaceBrowseMapClusters;
};

/**
 * Splits a map result into pins and bubbles. A cluster with one usable
 * listing is drawn as that listing's price pin, so the total never drifts.
 */
export const selectMapResult = (result: MarketplaceBrowseMapQuery): MarketplaceMapResult => {
  const mapSearch = result.marketplace.mapSearch;
  if (mapSearch.__typename === 'MarketplaceMapSearchMarkersResult') {
    return { markers: mapSearch.markers, clusters: [] };
  }
  if (mapSearch.__typename === 'MarketplaceMapSearchClustersResult') {
    const markers: MarketplaceBrowseMapMarkers = [];
    const clusters: MarketplaceBrowseMapClusters = [];
    mapSearch.clusters.forEach((cluster) => {
      if (cluster.listing) {
        markers.push(cluster.listing);
      } else {
        clusters.push(cluster);
      }
    });
    return { markers, clusters };
  }
  throw new Error('Marketplace map search returned an unexpected result');
};

const buildMapSearchInput = (
  mapInput: NonNullable<MarketplaceBrowseVariables['mapInput']>,
  mapMode: MarketplaceMapMode,
  mapViewport?: MarketplaceGeoPointInput[],
): NonNullable<MarketplaceBrowseVariables['mapInput']> => {
  if (!mapInput.mode.markers) return mapInput;
  const area = mapInput.mode.markers.area;
  let viewport = area.vertices ?? mapViewport;
  if (area.bounds) {
    const { north, south, east, west } = area.bounds;
    viewport = [
      { lat: north, lon: west },
      { lat: north, lon: east },
      { lat: south, lon: east },
      { lat: south, lon: west },
      { lat: north, lon: west },
    ];
  }
  if (mapMode === 'markers' || mapMode === 'unclustered' || !viewport) {
    return mapInput;
  }
  return {
    filters: mapInput.filters,
    mode: { adaptive: { viewport, area, ...(mapMode === 'screen' ? { markerLimit: 500 } : {}) } },
  };
};

const withoutStatusFilters = <Input extends { filters?: MarketplaceListingFiltersInput | null }>(
  input: Input,
): Input => {
  if (!input.filters) return input;
  const { includePending: _includePending, statusesAny: _statusesAny, ...filters } = input.filters;
  return { ...input, filters };
};

/**
 * Only the refresh layout has the status pills, so a markers search, which is
 * the legacy layout's, leaves out their filters. The URL keeps them, so the
 * refresh layout still applies them.
 */
const legacySearchVariables = ({
  listInput,
  mapInput,
}: MarketplaceBrowseVariables): MarketplaceBrowseVariables => ({
  listInput: withoutStatusFilters(listInput),
  ...(mapInput ? { mapInput: withoutStatusFilters(mapInput) } : {}),
});

/** The search that a map mode sends for a set of search inputs. */
export const browseSearchVariables = (
  variables: MarketplaceBrowseVariables,
  mapMode: MarketplaceMapMode,
): MarketplaceBrowseVariables =>
  mapMode === 'markers' ? legacySearchVariables(variables) : variables;

const fetchBrowseListings = ({
  request,
  input,
  signal,
  mapMode,
}: {
  request: MarketplaceBrowseRequest;
  input: MarketplaceBrowseVariables['listInput'];
  signal: AbortSignal;
  mapMode: MarketplaceMapMode;
}) =>
  mapMode !== 'markers'
    ? request(MarketplaceBrowseRefreshDocument, { input }, signal)
    : request(MarketplaceBrowseDocument, { input }, signal);

const fetchInitialBrowse = async ({
  variables,
  signal,
  request,
  mapMode,
  mapViewport,
  mapQueryClient,
  onMapResult,
  cachedListings,
}: MarketplaceBrowseRequestOptions): Promise<MarketplaceInitialBrowsePage> => {
  const browsePromise = cachedListings
    ? Promise.resolve({ marketplace: { listings: cachedListings } })
    : fetchBrowseListings({
        request,
        input: variables.listInput,
        signal,
        mapMode,
      });
  let mapPromise: Promise<MarketplaceBrowseMapQuery> | undefined;
  if (variables.mapInput) {
    const input = buildMapSearchInput(variables.mapInput, mapMode, mapViewport);
    const mapVariables = {
      input,
      ...(mapMode === 'screen' || mapMode === 'unclustered' ? { includePhotos: false } : {}),
    };
    mapPromise =
      mapQueryClient && mapMode !== 'markers'
        ? mapQueryClient.query({
            queryKey: [...marketplaceMapQueryKey, input, mapVariables.includePhotos !== false],
            queryFn: ({ signal: mapSignal }) =>
              request(MarketplaceBrowseMapDocument, mapVariables, mapSignal),
            staleTime: 30_000,
            gcTime: 30_000,
          })
        : request(MarketplaceBrowseMapDocument, mapVariables, signal);
    mapPromise = mapPromise.then((map) => {
      if (!signal.aborted) onMapResult?.(selectMapResult(map));
      return map;
    });
  }
  const [browse, map] = await Promise.all([browsePromise, mapPromise]);
  const mapResult = map ? selectMapResult(map) : null;

  return {
    kind: 'initial',
    listings: browse.marketplace.listings,
    mapMarkers: mapResult?.markers ?? null,
    mapClusters: mapResult?.clusters ?? null,
  };
};

export const marketplaceBrowseQueryOptions = ({
  variables: searchVariables,
  request = requestZeusFromBrowser,
  mapMode = 'markers',
  mapViewport,
  mapQueryClient,
  onMapResult,
}: MarketplaceBrowseQueryOptions) => {
  const variables = browseSearchVariables(searchVariables, mapMode);
  const viewport = variables.mapInput
    ? buildMapSearchInput(variables.mapInput, mapMode, mapViewport).mode.adaptive?.viewport
    : undefined;
  const buffered = mapMode === 'screen' || mapMode === 'unclustered';
  const cached = buffered
    ? (['adaptive', 'screen', 'unclustered'] as const)
        .filter((mode) => mode !== mapMode)
        .map((mode) =>
          mapQueryClient?.getQueryState<InfiniteData<MarketplaceBrowsePage, number>>([
            ...marketplaceBrowseQueryKey,
            variables,
            mode,
            variables.mapInput
              ? (buildMapSearchInput(variables.mapInput, mode, mapViewport).mode.adaptive
                  ?.viewport ?? null)
              : null,
          ]),
        )
        .filter((state) => state?.data?.pages[0]?.kind === 'initial')
        .sort((a, b) => (b?.dataUpdatedAt ?? 0) - (a?.dataUpdatedAt ?? 0))[0]
    : undefined;
  return infiniteQueryOptions({
    // The legacy key shape is kept for the markers mode so SSR hydration matches.
    queryKey:
      mapMode === 'markers'
        ? [...marketplaceBrowseQueryKey, variables]
        : [...marketplaceBrowseQueryKey, variables, mapMode, viewport ?? null],
    queryFn: async ({ signal, pageParam }): Promise<MarketplaceBrowsePage> => {
      const cachedPages =
        cached && Date.now() - cached.dataUpdatedAt < 30_000 ? cached.data : undefined;
      const cachedPage = cachedPages?.pages[cachedPages.pageParams.indexOf(pageParam)];
      if (pageParam !== 0) {
        if (cachedPage) return { kind: 'more', listings: cachedPage.listings };
        const data = await fetchBrowseListings({
          request,
          signal,
          mapMode,
          input: { ...variables.listInput, page: pageParam },
        });
        return { kind: 'more', listings: data.marketplace.listings };
      }
      return fetchInitialBrowse({
        variables,
        signal,
        request,
        mapMode,
        mapViewport,
        mapQueryClient,
        onMapResult,
        cachedListings: cachedPage?.listings,
      });
    },
    initialData: cached?.data,
    // Reused listing pages still need the new mode's map response.
    initialDataUpdatedAt: 0,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (mapMode !== 'screen' && mapMode !== 'unclustered') return undefined;
      const next = lastPage.listings.nextPage;
      const current = lastPageParam || (variables.listInput.page ?? 0);
      return next != null && next > current ? next : undefined;
    },
    refetchOnMount: buffered,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: 30_000,
    staleTime: 30_000,
  });
};
